import { isComplete, serviceClass } from '../../data/domain';
import { useJobs } from '../../data/hooks';
import { hourLabel, longDate, minutesOf, plural, timeLabel } from '../../lib/format';
import { printSection } from '../../lib/print';
import { Modal } from '../../ui/Modal';
import { useUI } from '../../ui/UIProvider';

/** A technician's hour-by-hour day sheet. Opening a job here switches to field mode to complete it. */
export function RunSheetModal({ tech, date }: { tech: string; date: string }) {
  const jobs = useJobs();
  const ui = useUI();
  const assigned = jobs.filter((job) => job.date === date && job.techName === tech && !job.cancelled).sort((a, b) => minutesOf(a.startTime) - minutesOf(b.startTime));
  // 7 AM to 6 PM, widened when a job falls outside those hours so nothing is hidden.
  const hours = assigned.map((job) => Math.floor(minutesOf(job.startTime) / 60));
  const first = Math.min(7, ...hours);
  const last = Math.max(18, ...hours);

  function openJob(jobId: string) {
    ui.close('runSheet');
    ui.open('jobEditor', { mode: 'edit', jobId, techMode: true, returnTo: { tech, date } });
  }

  return (
    <Modal id="runSheetModal" size="lg" className="board-modal run-sheet-detail" onClose={() => ui.close('runSheet')} labelledBy="runSheetTitle">
      <header className="modal-header modal-header-actions">
        <div><p className="eyebrow">{longDate(date)}</p><h2 id="runSheetTitle">{tech}</h2><p className="form-help">{plural(assigned.length, 'job')} · open a job to complete it in the field</p></div>
        <button className="btn btn-secondary" id="printDaySheet" onClick={() => printSection('run-sheet')}>Print day sheet</button>
      </header>
      <div className="hour-list">
        {Array.from({ length: last - first + 1 }, (_, offset) => {
          const hour = first + offset;
          const entries = assigned.filter((job) => Math.floor(minutesOf(job.startTime) / 60) === hour);
          return (
            <div className="hour-slot" key={hour}>
              <span className="hour-label">{hourLabel(hour)}</span>
              <div className="hour-events">
                {entries.map((job) => {
                  const complete = isComplete(job);
                  return (
                    <button key={job.id} className={`job-event ${serviceClass(job)}${complete ? ' completed' : ''}`} onClick={() => openJob(job.id)}>
                      <b><time>{timeLabel(job.startTime)}</time> {job.serviceCode}{complete ? ' · Completed' : ''}</b>
                      <small>{job.detail || job.serviceCode} · {job.clientName} · {job.duration || 60} min{job.notes ? ` · ${job.notes}` : ''}</small>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
